// ✅ FILE: src/pages/what-we-do/FocusAreaDetail.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar as CalendarIcon } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  API_URL,
  formatDate,
  getCategoryLabel,
  getStatusColor,
} from "../../lib/utils";

import { focusAreas, focusAreaBySlug, WHAT_WE_DO_BASE } from "./focus-areas";

const fallbackImage =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200&auto=format&fit=crop&q=80";

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");

const getImageSrc = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") return fallbackImage;
  const clean = imageUrl.trim();
  if (!clean) return fallbackImage;
  if (/^https?:\/\//i.test(clean)) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const normalizeEventImageUrl = (url) => getImageSrc(url);

function BulletList({ items, dotClass = "bg-amber-600" }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="space-y-2 text-stone-700">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <span className={`mt-2 inline-block w-2 h-2 rounded-full ${dotClass}`} />
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function PillGrid({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-stone-700"
        >
          {item}
        </div>
      ))}
    </div>
  );
}

export const FocusAreaDetail = () => {
  const { slug } = useParams();
  const area = focusAreaBySlug(slug);

  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingSide, setLoadingSide] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSidebar = async () => {
      setLoadingSide(true);
      try {
        const [projectsRes, eventsRes] = await Promise.all([
          fetch(`${API_URL}/projects?is_active=true`),
          fetch(`${API_URL}/events`),
        ]);

        const projectsData = projectsRes.ok
          ? await projectsRes.json().catch(() => [])
          : [];
        const eventsData = eventsRes.ok
          ? await eventsRes.json().catch(() => [])
          : [];

        if (!mounted) return;

        setProjects(Array.isArray(projectsData) ? projectsData : []);
        setEvents(Array.isArray(eventsData) ? eventsData : []);
      } catch (e) {
        if (!mounted) return;
        setProjects([]);
        setEvents([]);
      } finally {
        if (!mounted) return;
        setLoadingSide(false);
      }
    };

    fetchSidebar();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const relatedProjects = useMemo(() => {
    if (!area) return [];
    const matchers = area.projectCategoryMatch || [];
    const list = [...projects];

    // Best: exact focus_area match
    let filtered = list.filter(
      (p) => p?.focus_area && matchers.includes(p.focus_area)
    );

    // Fallback: partial match
    if (filtered.length === 0) {
      const needles = matchers.map((m) => (m || "").toLowerCase());
      filtered = list.filter((p) => {
        const fa = (p?.focus_area || "").toLowerCase();
        return needles.some((n) => n && fa.includes(n));
      });
    }

    return filtered.slice(0, 4);
  }, [projects, area]);

  const upcomingEvents = useMemo(() => {
    const list = [...events];
    const upcoming = list.filter(
      (e) => (e?.status || "").toLowerCase() === "upcoming"
    );
    const rest = list.filter(
      (e) => (e?.status || "").toLowerCase() !== "upcoming"
    );
    return [...upcoming, ...rest].slice(0, 4);
  }, [events]);

  if (!area) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <h1 className="font-heading text-2xl font-bold text-stone-900 mb-3">
            Focus area not found
          </h1>
          <p className="text-stone-600 mb-6">
            The page you are looking for does not exist.
          </p>
          <Button
            asChild
            className="bg-amber-700 hover:bg-amber-800 text-white rounded-full"
          >
            <Link to={WHAT_WE_DO_BASE}>Back to What We Do</Link>
          </Button>
        </div>
      </div>
    );
  }

  const Icon = area.icon;

  return (
    <div data-testid="focus-area-detail-page" className="bg-white text-stone-900">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-28 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${area.image}')` }}
        />
        <div className={`absolute inset-0 bg-gradient-to-br ${area.heroOverlay}`} />
        {/* extra contrast so text never looks black */}
        <div className="absolute inset-0 bg-black/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to={WHAT_WE_DO_BASE}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to What We Do
          </Link>

          <div className="max-w-3xl">
            {/* ✅ HERO BADGE: glass + white text (fixes your black badge problem) */}
            <div className="flex items-center gap-3 mb-5">
              <span className={area.heroBadge?.wrap || "inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold tracking-wide text-white/95 backdrop-blur-sm"}>
                <Icon className={`w-4 h-4 ${area.heroBadge?.icon || "text-amber-300"}`} />
                <span className="inline-flex items-center gap-2">
                  <span className={area.heroBadge?.dot || "h-2 w-2 rounded-full bg-amber-300"} />
                  {area.shortLabel}
                </span>
              </span>
            </div>

            {/* ✅ HERO HEADING: white + gold accent */}
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold mb-5 leading-tight text-white drop-shadow-[0_18px_45px_rgba(0,0,0,0.55)]">
              {area.details?.headline || area.title}
            </h1>

            <p className="text-white/85 text-lg leading-relaxed max-w-2xl drop-shadow-[0_14px_38px_rgba(0,0,0,0.45)]">
              {area.description}
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                asChild
                className="bg-white text-stone-900 hover:bg-white/90 rounded-full px-7"
              >
                <Link to="/get-involved#donate">
                  Donate <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/15 hover:text-white backdrop-blur"
              >
                <Link to="/get-involved#volunteer">Volunteer</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content + Sidebar */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr,320px] gap-10">
          {/* Main */}
          <div>
            <Card className={`border ${area.borderColor} rounded-3xl shadow-sm`}>
              <CardContent className="p-7 md:p-10">
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className={`w-14 h-14 ${area.color} rounded-2xl flex items-center justify-center ring-8 ${area.iconRing} shrink-0`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 leading-tight">
                      {area.title}
                    </h2>
                    <p className="text-stone-600 mt-2 leading-relaxed">
                      {area.details?.intro || area.description}
                    </p>
                  </div>
                </div>

                {/* Why it matters */}
                {Array.isArray(area.details?.whyItMatters) &&
                  area.details.whyItMatters.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        Why This Matters
                      </h3>
                      <BulletList items={area.details.whyItMatters} />
                    </div>
                  )}

                {/* Who we serve */}
                {Array.isArray(area.details?.whoWeServe) &&
                  area.details.whoWeServe.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        Who We Serve
                      </h3>
                      <BulletList items={area.details.whoWeServe} />
                    </div>
                  )}

                {/* Key activities */}
                {Array.isArray(area.details?.keyActivities) &&
                  area.details.keyActivities.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        Key Activities
                      </h3>
                      <PillGrid items={area.details.keyActivities} />
                    </div>
                  )}

                {/* Our approach */}
                {Array.isArray(area.details?.ourApproach) &&
                  area.details.ourApproach.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        Our Approach
                      </h3>
                      <BulletList items={area.details.ourApproach} />
                    </div>
                  )}

                {/* Safeguarding commitments */}
                {Array.isArray(area.details?.safeguardingCommitments) &&
                  area.details.safeguardingCommitments.length > 0 && (
                    <div className="mt-8 rounded-3xl border border-stone-200 bg-stone-50 p-6">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-3">
                        Safeguarding Commitments
                      </h3>
                      <BulletList
                        items={area.details.safeguardingCommitments}
                        dotClass="bg-stone-900"
                      />
                    </div>
                  )}

                {/* Outcomes */}
                {Array.isArray(area.details?.outcomes) &&
                  area.details.outcomes.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        Expected Outcomes
                      </h3>
                      <BulletList items={area.details.outcomes} />
                    </div>
                  )}

                {/* Indicators */}
                {Array.isArray(area.details?.indicators) &&
                  area.details.indicators.length > 0 && (
                    <div className="mt-8">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-4">
                        How We Measure Impact
                      </h3>
                      <BulletList items={area.details.indicators} />
                    </div>
                  )}

                {/* How to support */}
                {Array.isArray(area.details?.howToSupport) &&
                  area.details.howToSupport.length > 0 && (
                    <div className="mt-10 rounded-3xl border border-amber-200 bg-amber-50 p-6 md:p-7">
                      <h3 className="font-heading text-xl font-bold text-stone-900 mb-3">
                        How You Can Support
                      </h3>
                      <p className="text-stone-700 mb-4">
                        Your support helps us scale impact and reach more people
                        with practical, measurable interventions.
                      </p>

                      <BulletList
                        items={area.details.howToSupport}
                        dotClass="bg-amber-700"
                      />

                      <div className="mt-6 flex flex-wrap gap-3">
                        <Button
                          asChild
                          className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-7"
                        >
                          <Link to="/get-involved#donate">
                            Donate <ArrowRight className="ml-2 w-4 h-4" />
                          </Link>
                        </Button>
                        <Button
                          asChild
                          variant="outline"
                          className="rounded-full border-amber-300 bg-white/50 hover:bg-white"
                        >
                          <Link to="/get-involved#volunteer">Volunteer</Link>
                        </Button>
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>

            {/* Related Projects */}
            <div className="mt-10">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h3 className="font-heading text-2xl font-bold text-stone-900">
                  Related Projects
                </h3>
                <Link
                  to="/projects"
                  className="text-amber-700 font-semibold hover:text-amber-800 inline-flex items-center"
                >
                  View all <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
              </div>

              {loadingSide ? (
                <div className="grid md:grid-cols-2 gap-5">
                  {[...Array(2)].map((_, i) => (
                    <div
                      key={i}
                      className="h-[180px] rounded-2xl bg-stone-100 animate-pulse"
                    />
                  ))}
                </div>
              ) : relatedProjects.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-5">
                  {relatedProjects.map((p) => (
                    <Card
                      key={p.id}
                      className="overflow-hidden rounded-2xl border border-stone-200 shadow-sm hover:shadow-lg transition"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={getImageSrc(p.image_url)}
                          alt={p.title || "Project"}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          {/* This badge is on WHITE background, so use your normal UI Badge */}
                          <Badge variant="secondary">
                            {p.focus_area || area.shortLabel}
                          </Badge>
                        </div>
                        <h4 className="font-heading text-lg font-bold text-stone-900 mb-2 line-clamp-2">
                          {p.title}
                        </h4>
                        <p className="text-stone-600 text-sm line-clamp-3 mb-4">
                          {p.excerpt || p.description || ""}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Link
                            className="text-amber-700 font-semibold hover:text-amber-800 inline-flex items-center"
                            to={`/projects/${p.slug || p.id}`}
                          >
                            Read more <ArrowRight className="ml-1 w-4 h-4" />
                          </Link>
                          <Link
                            className="text-stone-700 font-semibold hover:text-stone-900"
                            to="/get-involved#donate"
                          >
                            Support
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-8 text-center text-stone-600">
                  No related projects yet. Check back soon.
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:sticky lg:top-24 h-fit space-y-6">
            {/* Focus Areas nav */}
            <Card className="rounded-2xl border border-stone-200 bg-white">
              <CardContent className="p-5">
                <h3 className="font-semibold text-stone-900 mb-3">What We Do</h3>

                <div className="space-y-2">
                  {focusAreas.map((a) => {
                    const active = a.slug === slug;
                    return (
                      <Link
                        key={a.slug}
                        to={`${WHAT_WE_DO_BASE}/${a.slug}`}
                        className={`block rounded-xl px-3 py-2 text-sm transition ${
                          active
                            ? "bg-amber-100 text-amber-800 font-semibold"
                            : "hover:bg-stone-100 text-stone-700"
                        }`}
                      >
                        {a.shortLabel}
                      </Link>
                    );
                  })}
                </div>

                <div className="mt-5 grid gap-3">
                  <Button
                    asChild
                    className="bg-amber-700 hover:bg-amber-800 text-white rounded-xl"
                  >
                    <Link to="/get-involved#donate">Donate</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-xl">
                    <Link to="/get-involved#volunteer">Volunteer</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sidebar Projects */}
            <Card className="rounded-2xl border border-stone-200 bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <h3 className="font-semibold text-stone-900">Projects</h3>
                  <Link
                    to="/projects"
                    className="text-sm text-amber-700 font-semibold hover:text-amber-800"
                  >
                    View all
                  </Link>
                </div>

                {loadingSide ? (
                  <div className="space-y-3">
                    {[...Array(2)].map((_, i) => (
                      <div
                        key={i}
                        className="h-16 rounded-xl bg-stone-100 animate-pulse"
                      />
                    ))}
                  </div>
                ) : relatedProjects.length > 0 ? (
                  <div className="space-y-3">
                    {relatedProjects.slice(0, 3).map((p) => (
                      <Link
                        key={p.id}
                        to={`/projects/${p.slug || p.id}`}
                        className="block rounded-xl border border-stone-200 hover:bg-stone-50 transition overflow-hidden"
                      >
                        <div className="flex gap-3 p-3">
                          <div className="w-16 h-14 rounded-lg overflow-hidden bg-stone-100 shrink-0">
                            <img
                              src={getImageSrc(p.image_url)}
                              alt={p.title || "Project"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-stone-900 line-clamp-2">
                              {p.title}
                            </p>
                            <p className="text-xs text-stone-500 mt-1 line-clamp-1">
                              {p.focus_area || area.shortLabel}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-600">No projects to show yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Sidebar Events */}
            <Card className="rounded-2xl border border-stone-200 bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <h3 className="font-semibold text-stone-900">Events</h3>
                  <Link
                    to="/events"
                    className="text-sm text-amber-700 font-semibold hover:text-amber-800"
                  >
                    View all
                  </Link>
                </div>

                {loadingSide ? (
                  <div className="space-y-3">
                    {[...Array(2)].map((_, i) => (
                      <div
                        key={i}
                        className="h-16 rounded-xl bg-stone-100 animate-pulse"
                      />
                    ))}
                  </div>
                ) : upcomingEvents.length > 0 ? (
                  <div className="space-y-3">
                    {upcomingEvents.slice(0, 3).map((e) => (
                      <Link
                        key={e.id}
                        to={`/events/${e.id}`}
                        className="block rounded-xl border border-stone-200 hover:bg-stone-50 transition overflow-hidden"
                      >
                        <div className="flex gap-3 p-3">
                          <div className="w-16 h-14 rounded-lg overflow-hidden bg-stone-100 shrink-0">
                            <img
                              src={normalizeEventImageUrl(e.image_url)}
                              alt={e.title || "Event"}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge className={getStatusColor(e.status)}>
                                {e.status}
                              </Badge>
                              <Badge variant="secondary">
                                {getCategoryLabel(e.category)}
                              </Badge>
                            </div>
                            <p className="text-sm font-semibold text-stone-900 line-clamp-2">
                              {e.title}
                            </p>
                            <p className="text-xs text-stone-500 mt-1 inline-flex items-center gap-1">
                              <CalendarIcon className="w-3.5 h-3.5" />
                              {e?.date ? formatDate(e.date) : "TBD"}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-600">No events to show yet.</p>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default FocusAreaDetail;